(() => {
  const TAG = /\[variant:([^\]]+)\]/i;

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const getTag = (element) => {
    const img = element.querySelector('img[alt]');
    if (!img) return null;

    const match = (img.alt || '').match(TAG);
    return match ? normalize(match[1]) : null;
  };

  const setVisible = (element, visible) => {
    if (!element) return;

    element.hidden = !visible;
    element.classList.toggle('hidden', !visible);
  };

  let productInfo;
  let sectionId;

  let gallery;
  let galleryViewer;
  let sliderList;
  let thumbnailSlider;

  let slides = [];
  let thumbnails = [];
  let modalItems = [];

  let availableColors = new Set();
  let mediaTags = new Map();

  let pendingColor = null;
  let frame = null;
  let currentColor = null;

  function buildIndex() {
    productInfo =
      document.querySelector(
        'product-info[id^="MainProduct-"]'
      ) ||
      document.querySelector(
        'product-info[data-section]'
      );

    if (!productInfo) return false;

    sectionId = productInfo.dataset.section;

    if (!sectionId) return false;

    gallery = document.getElementById(
      `MediaGallery-${sectionId}`
    );

    if (!gallery) return false;

    galleryViewer = document.getElementById(
      `GalleryViewer-${sectionId}`
    );

    sliderList = document.getElementById(
      `Slider-Gallery-${sectionId}`
    );

    if (!sliderList) return false;

    thumbnailSlider = document.getElementById(
      `GalleryThumbnails-${sectionId}`
    );

    gallery.style.overflowAnchor = 'none';

    slides = Array.from(sliderList.children)
      .filter((element) =>
        element.matches('li[data-media-id]')
      )
      .map((element) => ({
        element,
        mediaId: element.dataset.mediaId,
        tag: getTag(element)
      }));

    if (!slides.length) return false;

    mediaTags = new Map();

    slides.forEach((slide) => {
      if (slide.mediaId) {
        mediaTags.set(
          slide.mediaId,
          slide.tag
        );
      }
    });

    availableColors = new Set(
      slides
        .map((slide) => slide.tag)
        .filter(Boolean)
    );

    if (!availableColors.size) {
      return false;
    }

    thumbnails = thumbnailSlider
      ? [
          ...thumbnailSlider.querySelectorAll(
            '[data-target]'
          )
        ].map((button) => ({
          element:
            button.closest('li') || button,
          button,
          mediaId: button.dataset.target
        }))
      : [];

    const modal =
      document.getElementById(
        `ProductModal-${sectionId}`
      );

    modalItems = modal
      ? [
          ...modal.querySelectorAll(
            '[data-media-id]'
          )
        ]
          .filter((element) =>
            element.querySelector('img')
          )
          .map((element) => ({
            element,
            tag: getTag(element)
          }))
      : [];

    return true;
  }

  function getInitialColor() {
    const selected =
      productInfo.querySelectorAll(`
        input[type="radio"]:checked,
        select option:checked
      `);

    for (const control of selected) {
      const value = normalize(
        control.value
      );

      if (
        availableColors.has(value)
      ) {
        return value;
      }
    }

    return null;
  }

  function applyColor(color) {
    if (
      !color ||
      !availableColors.has(color)
    ) {
      return;
    }

    if (color === currentColor) {
      return;
    }

    currentColor = color;

    let firstColorSlide = null;
    let activeSlide = null;

    slides.forEach((slide) => {
      if (
        slide.element.classList.contains(
          'is-active'
        )
      ) {
        activeSlide = slide;
      }
    });

    slides.forEach((slide) => {
      const visible =
        !slide.tag ||
        slide.tag === color;

      setVisible(
        slide.element,
        visible
      );

      if (
        !firstColorSlide &&
        slide.tag === color
      ) {
        firstColorSlide = slide;
      }
    });

    thumbnails.forEach((thumbnail) => {
      const tag =
        mediaTags.get(
          thumbnail.mediaId
        );

      const visible =
        !tag ||
        tag === color;

      setVisible(
        thumbnail.element,
        visible
      );
    });

    modalItems.forEach((item) => {
      const visible =
        !item.tag ||
        item.tag === color;

      setVisible(
        item.element,
        visible
      );
    });

    if (
      activeSlide &&
      activeSlide.element.hidden &&
      firstColorSlide
    ) {
      slides.forEach((slide) => {
        slide.element.classList.remove(
          'is-active'
        );
      });

      firstColorSlide.element.classList.add(
        'is-active'
      );

      sliderList.scrollLeft =
        firstColorSlide.element.offsetLeft;

      thumbnails.forEach(
        (thumbnail) => {
          if (!thumbnail.button) return;

          if (
            thumbnail.mediaId ===
            firstColorSlide.mediaId
          ) {
            thumbnail.button.setAttribute(
              'aria-current',
              'true'
            );
          } else {
            thumbnail.button.removeAttribute(
              'aria-current'
            );
          }
        }
      );
    }

    requestAnimationFrame(() => {
      if (
        typeof galleryViewer
          ?.resetPages === 'function'
      ) {
        galleryViewer.resetPages();
      }

      if (
        typeof thumbnailSlider
          ?.resetPages === 'function'
      ) {
        thumbnailSlider.resetPages();
      }
    });
  }

  function scheduleColor(color) {
    if (
      !availableColors.has(color)
    ) {
      return;
    }

    pendingColor = color;

    if (frame !== null) return;

    frame = requestAnimationFrame(() => {
      frame = null;

      const nextColor =
        pendingColor;

      pendingColor = null;

      applyColor(nextColor);
    });
  }

  function init() {
    if (!buildIndex()) return;

    /*
      Shopify/Liquid has already hidden the wrong
      colors before the first paint.

      Now JS creates the exact same state using
      Dawn's .hidden class.
    */
    const initialColor =
      getInitialColor();

    if (initialColor) {
      applyColor(initialColor);

      /*
        JS now owns gallery visibility.

        This disables the temporary Liquid CSS
        without causing a flash because applyColor()
        has already hidden the unwanted slides.
      */
      gallery.dataset.variantMediaReady =
        'true';
    }

    document.addEventListener(
      'change',
      (event) => {
        if (
          event.target.closest(
            'product-info'
          ) !== productInfo
        ) {
          return;
        }

        scheduleColor(
          normalize(event.target.value)
        );
      }
    );

    document.addEventListener(
      'click',
      (event) => {
        const swatch =
          event.target.closest(
            '[data-option-value]'
          );

        if (
          !swatch ||
          swatch.closest(
            'product-info'
          ) !== productInfo
        ) {
          return;
        }

        scheduleColor(
          normalize(
            swatch.getAttribute(
              'data-option-value'
            )
          )
        );
      }
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();